import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { AnalysisAPI } from '../services/api';
import { useAuth } from './AuthContext';

export interface TourStep {
  id: string;
  title: string;
  content: string;
  target: string; // CSS selector or element ID
  placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
  action?: () => void; // Optional action to perform when step is shown
  beforeNext?: () => boolean; // Optional validation before proceeding
  showNext?: boolean;
  showPrev?: boolean;
  showSkip?: boolean;
}

interface TourState {
  isActive: boolean;
  currentStep: number;
  steps: TourStep[];
  isCompleted: boolean;
}

interface TourContextType {
  tourState: TourState;
  startTour: (steps: TourStep[]) => void;
  nextStep: () => void;
  prevStep: () => void;
  skipTour: () => void;
  endTour: () => void;
  goToStep: (stepIndex: number) => void;
  getCurrentStep: () => TourStep | null;
  isLastStep: () => boolean;
  isFirstStep: () => boolean;
}

const initialState: TourState = {
  isActive: false,
  currentStep: 0,
  steps: [],
  isCompleted: false,
};

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [tourState, setTourState] = useState<TourState>(initialState);
  const { dispatch, getCurrentUser } = useAuth();

  const startTour = useCallback((steps: TourStep[]) => {
    setTourState({
      isActive: true,
      currentStep: 0,
      steps,
      isCompleted: false,
    });
  }, []);

  const endTour = useCallback(async () => {
    setTourState(prev => ({
      ...prev,
      isActive: false,
      isCompleted: true,
    }));
    
    // Call API to mark onboarding as completed
    try {
      const response = await AnalysisAPI.completeOnboarding();
      if (response.success) {
        console.log('Onboarding marked as completed successfully');
        
        // Fetch fresh user data from the server to ensure we have the updated state
        // This is more reliable than trying to manually update cached user data
        try {
          await getCurrentUser();
          console.log('User data refreshed with updated onboarding status');
          
          // Scroll to top of page for clean dashboard view
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
        } catch (fetchError) {
          console.error('Error refreshing user data:', fetchError);
          // Fallback: reload the page to get fresh data
          console.log('Reloading page to get fresh user data...');
          setTimeout(() => window.location.reload(), 1000);
        }
      } else {
        console.error('Failed to mark onboarding as completed:', response.error);
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  }, [dispatch, getCurrentUser]);

  const nextStep = useCallback(() => {
    setTourState(prev => {
      const currentStep = prev.steps[prev.currentStep];
      
      // Check if there's a beforeNext validation
      if (currentStep?.beforeNext && !currentStep.beforeNext()) {
        return prev; // Don't proceed if validation fails
      }

      const nextStepIndex = prev.currentStep + 1;
      
      if (nextStepIndex >= prev.steps.length) {
        // Tour completed - call endTour to handle API call
        endTour();
        return prev; // endTour will handle the state update
      }
      
      return {
        ...prev,
        currentStep: nextStepIndex,
      };
    });
  }, [endTour]);

  const prevStep = useCallback(() => {
    setTourState(prev => {
      const prevStepIndex = prev.currentStep - 1;
      
      if (prevStepIndex < 0) {
        return prev; // Can't go before first step
      }
      
      return {
        ...prev,
        currentStep: prevStepIndex,
      };
    });
  }, []);

  const skipTour = useCallback(() => {
    setTourState(prev => ({
      ...prev,
      isActive: false,
      isCompleted: false,
    }));
  }, []);

  const goToStep = useCallback((stepIndex: number) => {
    setTourState(prev => {
      if (stepIndex < 0 || stepIndex >= prev.steps.length) {
        return prev; // Invalid step index
      }
      
      return {
        ...prev,
        currentStep: stepIndex,
      };
    });
  }, []);

  const getCurrentStep = useCallback((): TourStep | null => {
    const { steps, currentStep } = tourState;
    return steps[currentStep] || null;
  }, [tourState]);

  const isLastStep = useCallback((): boolean => {
    return tourState.currentStep === tourState.steps.length - 1;
  }, [tourState]);

  const isFirstStep = useCallback((): boolean => {
    return tourState.currentStep === 0;
  }, [tourState]);

  const value: TourContextType = {
    tourState,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    endTour,
    goToStep,
    getCurrentStep,
    isLastStep,
    isFirstStep,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};